import React, { useContext, useEffect, useMemo } from 'react';

import style from './PlanComparison.module.scss';
import stylePlan from '../Plans/Plan.module.scss';
import { getCostBlock } from '../Cost';
import { AppContext } from '../../context/AppContext';
import classnames from 'classnames';
import arrows from '../../assets/images/arrows.gif';
import { seams } from '../../constants';

import checkMark from '../../assets/images/check-mark.svg';
import cross from '../../assets/images/cross.svg';
import { IFeature } from '../../react-app-env';
import classNames from 'classnames';
import { getName } from '../../utils/getPlanName';
import { IPlan } from '../../model';
import type { Instance } from 'tippy.js';

interface IProps {
  plans: IPlan[];
  isShowAll?: boolean;
  sport: string;
}

let previewWidget: TooltipPreviewInstance;

// @ts-ignore
if (typeof TooltipPreview === 'function') {
  // @ts-ignore
  previewWidget = new TooltipPreview();
}

let instances: Instance[] = [];

const PlanComparison = ({plans, sport}: IProps) => {
  const headerRef = React.createRef<HTMLTableSectionElement>();
  const tableRef = React.createRef<HTMLTableElement>();
  const contextData = useContext(AppContext);
  const {yearly, currentSubscriptions, baseCheckoutPath} = contextData || {};

  const features = useMemo<IFeature[]>(() => {
    const planIds = plans.map(plan => plan.plan_id);

    const featuresCount: Record<string, number> = {};

    const allFeatures = (planIds
      .map(planId => window.plans?.[planId])
      .filter(Boolean) as string[][])
      .reduce(
        (val, featureList) => {
          if (Array.isArray(featureList)) {
            featureList.forEach((feature: string) => {
              featuresCount[feature] = featuresCount[feature] ? featuresCount[feature] + 1 : 1
            })
            return val.concat(featureList.filter(Boolean))
          }
          return val;
        }, []
      );


    // @ts-ignore
    const features: string[] = [...new Set(allFeatures)].filter(Boolean);
    // @ts-ignore
    return (features.map(featureId => window.features?.[featureId]).filter(Boolean) as IFeature[]).sort((a, b) => {
      const res = Object.keys(currentSubscriptions || {}).length ? featuresCount[a.id] - featuresCount[b.id] : featuresCount[b.id] - featuresCount[a.id]; // Sort - more plans - first. UPD: If it has current plan - otherwise - fewer plans - first
      if (res !== 0) return res;

      return a.priority[sport] - b.priority[sport] // Sort - less priority - first
    });
  }, [plans, currentSubscriptions]);

  useEffect(() => {
    previewWidget?.destroy(instances || []);
    setTimeout(() => {
      const newInstances = previewWidget?.init('[data-preview-id]');
      if (newInstances && Array.isArray(newInstances)) {
        instances = newInstances;
      }
    }, 100)
  }, [features]);

  const scroll = () => {
    const header = headerRef.current;
    const tableRect = tableRef.current?.getBoundingClientRect();

    if (tableRect && header) {
      const tableTop = tableRect?.top;
      const tableBottom = tableRect?.bottom;
      if (tableTop < 70 && tableBottom > ((header.offsetHeight ?? 0) + 70)) {
        header.classList.add('is-sticky');
        header.style.transform = `translateY(${Math.abs(70 - tableTop)}px)`;
      } else {
        header.classList.remove('is-sticky');
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', scroll);
    return () => {
      window.removeEventListener('scroll', scroll);
    };
  }, [headerRef, tableRef]);

  if (!features.length) return <></>;


  const hasSubscriptions = Object.keys(currentSubscriptions || {}).length > 0;

  return (
    <div className={style.scroll_area}>
      <div className={style.table__wrapper}>
        <table className={classNames(style.table, {[style.wide]: plans.length >= 2})} ref={tableRef}>
          {/* @ts-ignore */}
          <thead ref={headerRef}>
          <tr>
            <th className={style.table_header}>What you get</th>
            {(plans.map(plan => plan.isAdditional ? plan.additional : plan).filter(Boolean) as IPlan[]).map((plan) => {
              const currentPrice = plan?.current?.price;
              const yearAmount = plan?.current?.interval === 'year'
                ? currentPrice
                  ? Number(currentPrice)
                  : plan?.prices?.yearly?.unit_amount ?? 0
                : plan?.prices?.yearly?.unit_amount ?? 0;

              const monthAmount = plan?.current?.interval === 'month'
                ? currentPrice
                  ? Number(currentPrice)
                  : plan?.prices?.monthly?.unit_amount ?? 0
                : plan?.prices?.monthly?.unit_amount ?? 0;

              let cost = getCostBlock({
                yearly,
                yearAmount: yearAmount,
                monthAmount: monthAmount,
                showDescription: false
              })
              if(!yearAmount && !monthAmount) { cost = <></>; }
              const priceObject = !yearly ? plan?.prices?.monthly : plan?.prices?.yearly;
              const isSeams = seams?.includes(String(plan?.plan_id));
              return (
                <th key={plan?.plan_id} className={classNames(style.table_header)}>
                  <div className={style.table_header__inner}>
                    <div className={classNames(stylePlan.title, style.title)}>{getName(plan)}</div>
                    <div className={classNames(stylePlan.cost, style.cost)}>{cost}</div>
                    <div className={style.spacer}></div>
                    {!plan.disabled && !plan?.current ? <div className={classnames(style.upgrade)}>
                      <a
                        href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}`}
                        className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                        {plan && (!plan?.current && hasSubscriptions)
                          ? (<>
                              <img className={style.desktop} src={arrows} alt=""
                                   style={{width: 40, height: 40, margin: '0 -5px 0 -15px'}}/>
                              Upgrade
                            </>
                          )
                          : (<>
                          <span
                            className={style.desktop}>{!isSeams ? 'Subscribe Now!' : 'Contest Sims Subscribe Now !'}</span>
                            <span className={style.mobile}>Buy!</span>
                          </>)}
                      </a>
                    </div> : ''}
                    {/*{(current?.subscription?.cancel_at ? (canceledAtDate ? <a*/}
                    {/*  href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}`}*/}
                    {/*  className={classnames('button-primary button-primary_md')}*/}
                    {/*  style={{width: '100%', marginTop: 10}}*/}
                    {/*>*/}
                    {/*  Don't Cancel Subscription*/}
                    {/*</a> : '') : '')}*/}
                  </div>
                </th>
              )
            })}
          </tr>
          </thead>
          <tbody>
          {features.map((feature) => <tr>
            {
              feature.previewId
                ? <td data-preview-id={feature.previewId}>{feature.title}</td>
                : <td>{feature.title}</td>
            }
            {plans.map((plan) => {
              const mainPlan = plan.isAdditional ? plan.additional : plan;
              // @ts-ignore
              if (window.plans?.[mainPlan.plan_id]?.includes(feature.id)) {
                return (<td>
                  <div className={style.icon}><img src={checkMark} alt={'Yes'}/></div>
                </td>)
              }
              return (
                <td>
                  <div className={style.icon}><img src={cross} alt={'No'}/></div>
                </td>
              )
            })}
          </tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlanComparison;
